import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { adminLogin, getAdminToken, clearAdminToken } from "../lib/api";
import { Lock, ArrowLeft } from "lucide-react";

export default function AdminGate({ children }) {
  const [authed, setAuthed] = useState(() => Boolean(getAdminToken()));
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = getAdminToken();
    if (!stored) return;
    adminLogin(stored).catch((err) => {
      if (err?.response?.status === 401) {
        clearAdminToken();
        setAuthed(false);
        toast.error("Session expired — please sign in again");
      }
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      await adminLogin(password);
      setAuthed(true);
      toast.success("Signed in");
    } catch (err) {
      const msg = err?.response?.status === 401 ? "Invalid password" : "Sign in failed";
      toast.error(msg);
      setPassword("");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = () => {
    clearAdminToken();
    setAuthed(false);
    setPassword("");
    toast.success("Signed out");
  };

  if (authed) {
    return (
      <>
        {children}
        <button
          onClick={logout}
          data-testid="admin-logout"
          className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase bg-white border border-[#0A0A0A] px-4 py-2 hover:bg-[#0A0A0A] hover:text-white transition-colors"
        >
          Sign out
        </button>
      </>
    );
  }

  return (
    <div
      data-testid="admin-gate"
      className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A] flex items-center justify-center px-6"
    >
      <div className="absolute top-6 left-6 md:top-8 md:left-10">
        <Link
          to="/"
          data-testid="admin-gate-back"
          className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase hover:opacity-60"
        >
          <ArrowLeft size={14} /> Site
        </Link>
      </div>

      <form
        onSubmit={submit}
        data-testid="admin-gate-form"
        className="w-full max-w-md bg-white border border-[#E5E5E5] p-8 md:p-12"
      >
        <div className="text-center mb-10">
          <Lock size={16} className="mx-auto mb-4 text-[#0A0A0A]" />
          <h1
            className="text-[#0A0A0A] uppercase mb-3"
            style={{
              fontFamily: "'Jost', 'Futura', 'Helvetica Neue', sans-serif",
              fontSize: "1.6rem",
              letterSpacing: "0.28em",
              paddingLeft: "0.28em",
              fontWeight: 200,
            }}
          >
            ADMIN
          </h1>
          <p className="text-xs tracking-[0.2em] uppercase text-[#666]">
            JAY-INK Academy · Private
          </p>
        </div>

        <label className="overline block mb-3">Password</label>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-gate-password"
          className="editorial-input"
          placeholder="••••••••"
        />

        <button
          type="submit"
          disabled={submitting || !password}
          data-testid="admin-gate-submit"
          className="mt-10 w-full inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-white px-8 py-4 text-xs tracking-[0.28em] uppercase font-medium hover:bg-[#333] transition-colors disabled:opacity-60"
        >
          {submitting ? "Signing in…" : "Enter"}
          <span>→</span>
        </button>
      </form>
    </div>
  );
}
